import { NgModule } from '@angular/core';
import { NoPreloading, RouterModule, Routes } from '@angular/router';
import { errorRouting } from '@app/public/error/error.route';
import { publicRouting } from '@app/public/public.route';
import { TeaserEditorComponent } from '@app/shared/components/teaser/teaser-editor/teaser-editor.component';
import { TeaserComponent } from '@app/shared/components/teaser/teaser.component';
import { PermissionCode } from '@app/shared/enums';
import { AuthGuard } from '@app/shared/guards/auth.guard';
import { DsoGuard } from '@app/shared/guards/dso.guard';
import { MunicipalityDeactivatedGuard } from '@app/shared/guards/municipality-deactivated.guard';
import { PermissionGuard } from '@app/shared/guards/permission.guard';
import { RedirectGuard } from '@app/shared/guards/redirect.guard';
import { ContactTabsComponent } from './contact-tabs/contact-tabs.component';
import { InnoliveTeaserComponent } from './dashboard/components/innolive-teaser/innolive-teaser.component';
import { DashboardComponent } from './dashboard/dashboard.component';
import { DesignWizardComponent } from './design-wizard/design-wizard.component';
import { EnergieMonitorComponent } from './energiemonitor/energiemonitor.component';
import { EnergyMapComponent } from './energy-map/energy-map.component';
import { EnergyMapCanDeactivateGuard } from './energy-map/guards/energy-map-can-deactivate.guard';
import { MainComponent } from './main/main.component';
import { ProductCatalogManagementComponent } from './product-catalog-management/product-catalog-management.component';
import { ServiceAppsComponent } from './service-apps/service-apps.component';
import { StorungenComponent } from './storungen/storungen.component';
import { EnergiemonitorCanActivateGuard } from './energiemonitor/guards/energiemonitor-canactivate.guard';
import { EnergieMonitorLegacyComponent } from './energiemonitor/_legacy/components/_energiemonitor.component';
import { ContinueEditingGuard } from './shared/guards/continue-editing.guard';

export const routes: Routes = [
  {
    path: '',
    canActivate: [AuthGuard, DsoGuard],
    canActivateChild: [MunicipalityDeactivatedGuard, PermissionGuard, AuthGuard, DsoGuard],
    canDeactivate: [ContinueEditingGuard],
    component: MainComponent,
    runGuardsAndResolvers: 'always',
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: '/instrumententafel',
        runGuardsAndResolvers: 'always',
      },
      {
        path: 'instrumententafel',
        component: DashboardComponent,
        runGuardsAndResolvers: 'always',
      },
      {
        path: 'energiekarte',
        component: EnergyMapComponent,
        canDeactivate: [EnergyMapCanDeactivateGuard],
        data: { permissionCodes: ['ENERGY_MAP'] },
        runGuardsAndResolvers: 'always',
      },
      {
        path: 'energiekarte/:id',
        component: EnergyMapComponent,
        canDeactivate: [EnergyMapCanDeactivateGuard],
        data: { permissionCodes: ['ENERGY_MAP', 'STREETLIGHT_PLANNER'] },
        runGuardsAndResolvers: 'always',
      },
      {
        path: 'effizienzanalyse',
        loadChildren: () => import('./efficiency-analysis/efficiency-analysis.module').then((m) => m.EfficiencyAnalysisModule),
        data: { permissionCodes: ['EFFICIENCY_ANALYSIS'] },
        runGuardsAndResolvers: 'always',
      },
      {
        path: 'kontakt',
        component: ContactTabsComponent,
        data: { permissionCodes: ['CONTACT'] },
        runGuardsAndResolvers: 'always',
      },
      {
        path: 'kontakt/:contactTypes',
        component: ContactTabsComponent,
        data: { permissionCodes: ['CONTACT'] },
        runGuardsAndResolvers: 'always',
      },
      {
        path: 'storungen',
        component: StorungenComponent,
        data: { permissionCodes: ['STORUNGEN'] },
        runGuardsAndResolvers: 'always',
      },
      {
        path: 'informationen',
        loadChildren: () => import('./information/information.module').then((m) => m.InformationModule),
        data: { permissionCodes: ['INFORMATION'] },
        runGuardsAndResolvers: 'always',
      },
      {
        path: 'energiemonitorv2',
        component: EnergieMonitorComponent,
        data: { permissionCodes: ['ENERGY_MONITOR'] },
        canActivate: [EnergiemonitorCanActivateGuard],
        runGuardsAndResolvers: 'always',
      },
      {
        path: 'energiemonitor',
        component: EnergieMonitorLegacyComponent,
        data: { permissionCodes: ['ENERGY_MONITOR'] },
        canActivate: [EnergiemonitorCanActivateGuard],
        runGuardsAndResolvers: 'always',
      },
      {
        path: 'inno-live-teaser',
        component: InnoliveTeaserComponent,
        data: { permissionCodes: ['INNO_LIVE'] },
        runGuardsAndResolvers: 'always',
      },
      {
        path: 'teaser/:component',
        component: TeaserComponent,
        data: {
          permissionCodes: [PermissionCode.SENSOR_DATA, PermissionCode.ROOM_MONITORING],
        },
        runGuardsAndResolvers: 'always',
      },
      {
        path: 'teaser/:component/editor',
        component: TeaserEditorComponent,
        data: {
          permissionCodes: [PermissionCode.SENSOR_TEASER_EDITOR, PermissionCode.ROOM_MONITORING_EDITOR],
        },
        runGuardsAndResolvers: 'always',
      },
      {
        path: 'produktkatalog',
        component: ProductCatalogManagementComponent,
        data: { permissionCodes: ['PRODUCT_CATALOGUE'] },
        runGuardsAndResolvers: 'always',
      },
      // {
      //   path: 'image-cropper',
      //   component: ImageCropperComponent,
      //   runGuardsAndResolvers: 'always',
      // },
      {
        path: 'grid-infra',
        loadChildren: () => import('./grid-infrastructure/grid-infrastructure.module').then((m) => m.GridInfrastructureModule),
      },
      {
        path: 'design-wizard',
        component: DesignWizardComponent,
        runGuardsAndResolvers: 'always',
      },
      {
        path: 'einstellungen',
        loadChildren: () => import('./management-tabs/management-tabs.module').then((m) => m.ManagementTabsModule),
      },
      {
        path: 'messdaten',
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: 'zählerstand',
          },
          {
            path: 'energiedaten',
            loadChildren: () => import('./energiedaten/_legacy/energiedaten-legacy.module').then((m) => m.EnergiedatenLegacyModule),
          },
          {
            path: 'energiedatenv2',
            loadChildren: () => import('./energiedaten/energiedaten.module').then((m) => m.EnergieDatenModule),
          },
          {
            path: 'sensordaten',
            loadChildren: () => import('./sensordaten/sensordaten.module').then((m) => m.SensordatenModule),
          },
          {
            path: 'zählerstand',
            loadChildren: () => import('./zahlerstand/zahlerstand.module').then((m) => m.ZahlerstandModule),
          },
        ],
      },
      {
        path: 'designer',
        loadChildren: () => import('./designer/designer.module').then((m) => m.DesignerModule),
      },
      {
        path: 'strassenbeleuchtung.schaeden',
        loadChildren: () => import('./socket-service-page/socket-service-page.module').then((m) => m.SocketServicePageModule),
      },
      {
        path: 'energy-guard',
        loadChildren: () => import('./energy-guard/energy-guard.module').then((m) => m.EnergyGuardModule),
      },
      {
        path: 'verbrauch',
        loadChildren: () => import('./meter-consumption/meter-consumption.module').then((m) => m.MeterConsumptionModule),
      },
      {
        path: 'warmedaten',
        loadChildren: () => import('./warmedaten/warmedaten.module').then((m) => m.WarmedatenModule),
      },
      {
        path: 'energie-management-optimum',
        loadChildren: () => import('./optimum/optimum.module').then((m) => m.OptimumModule),
      },
      {
        path: 'services',
        component: ServiceAppsComponent,
        runGuardsAndResolvers: 'always',
        data: { permissionCodes: ['SERVICES'] },
      },
      {
        path: 'documents',
        loadChildren: () => import('./my-documents/my-documents.module').then((m) => m.MyDocumentsModule),
      },
      {
        path: 'energiereport',
        loadChildren: () => import('./energy-report/energy-report.module').then((m) => m.EnergyReportModule),
      },
      {
        path: 'lieferstellenverwaltung',
        loadChildren: () => import('./lieferstellenverwaltung/lieferstellenverwaltung.module').then((m) => m.LieferstellenverwaltungModule),
      },
      {
        path: 'loesungsfinder',
        loadChildren: () => import('./solutionfinder/solutionfinder.module').then((m) => m.SolutionFinderModule),
      },
      {
        path: 'antraege',
        loadChildren: () => import('./antraege/antraege.module').then((m) => m.AntraegeModule),
      },
      {
        path: 'externe',
        loadChildren: () => import('./externe/externe.module').then((m) => m.ExterneModule),
      },
      {
        path: 'finanzreport',
        loadChildren: () => import('./finanzreport/finanzreport.module').then((m) => m.FinanceReportModule),
      },
    ],
  },
  publicRouting,
  errorRouting,
  {
    path: 'public/registrationrequest',
    loadChildren: () => import('./registration-request/registration-request.module').then((m) => m.RegistrationRequestModule),
  },
  {
    path: 'registrationrequest',
    loadChildren: () => import('./registration-request/registration-request.module').then((m) => m.RegistrationRequestModule),
  },
  {
    path: 'dso',
    loadChildren: () => import('./dso/dso.module').then((m) => m.DsoModule),
  },
  {
    path: 'app',
    canActivate: [RedirectGuard],
    component: RedirectGuard,
    data: {
      externalUrl: '/schadensmelder/home',
    },
    children: [
      {
        path: ':#',
        canActivate: [RedirectGuard],
        component: RedirectGuard,
        data: {
          externalUrl: '/schadensmelder/home',
        },
      },
    ],
  },
  { path: '**', redirectTo: 'error/404' },
];

@NgModule({
  exports: [RouterModule],
  imports: [
    RouterModule.forRoot(routes, {
      preloadingStrategy: NoPreloading,
      onSameUrlNavigation: 'reload',
      relativeLinkResolution: 'legacy',
    }),
  ],
})
export class AppRoutingModule {}
